import React from 'react';
import ConstrainedChips from '../../ConstrainedChips';
import { TARGET_DEFS, CONDITIONS, DEVICES, LIFESTYLE_FOCUS, MEDICATION_CLASSES, PATHWAY_META } from '../../../data/carePlanOptions';
import { normalizeCondition } from '@/logic/alignmentConditions';
import { formatTargetDisplay, buildEditPlan, approverDisplayName } from '../shared/carePlanFormat';
import EmptyState from '../shared/EmptyState';

// Care Plan — view for all roles, edit for clinician. The editable draft
// (editPlan/editingPlan) lives in the shell so it survives tab switches; the
// save is routed back through onSaveCarePlan.
export default function CarePlanTab({
  carePlan,
  carePlanContext,           // Tier-1 /care-plans GET (.data) — approval + applied_pathways
  appliedPathways = [],
  userRole,
  editPlan, setEditPlan,
  editingPlan, setEditingPlan,
  planSaving,
  onSaveCarePlan,
  onOpenPathway,
}) {
  if (!carePlan) {
    return <EmptyState title="No care plan on file yet." />;
  }

  const canEdit = userRole === 'clinician' && !!editPlan;
  const riskColors = { standard: '#22C55E', elevated: '#F59E0B', high: '#F97316', critical: '#EF4444' };
  const riskColor = riskColors[carePlan.risk_level] || '#9CA3AF';
  const exercise = carePlan.exercise_plan || {};
  const nutrition = carePlan.nutrition_plan || {};
  const planTargets = carePlan.targets ? Object.entries(carePlan.targets) : [];
  const approvedBy = carePlanContext?.approved_by ?? carePlan.approved_by;
  const approvedAt = carePlanContext?.approved_at ?? carePlan.approved_at;
  const isApproved = !!(approvedBy && approvedAt);
  const approvedByName = approverDisplayName(approvedBy);
  const fieldLabel = { display: 'block', marginBottom: 4, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--wi-text-secondary)' };
  const chipStyle = { display: 'inline-flex', alignItems: 'center', height: 26, padding: '0 10px', borderRadius: 6, fontSize: 12, fontWeight: 500, marginRight: 4, marginBottom: 4 };
  const chipRead = (text, bg, color) => (
    <span key={text} style={{ ...chipStyle, background: bg, color }}>{text}</span>
  );
  const inputStyle = { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--wi-divider)', fontSize: 13, fontFamily: 'var(--wi-font-family)', outline: 'none' };

  const openPathway = () => {
    if (editingPlan && carePlan) {
      setEditPlan(buildEditPlan(carePlan));
      setEditingPlan(false);
    }
    onOpenPathway();
  };

  return (
    <>
      {/* Section header with edit toggle */}
      <div style={{ margin: '4px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="eyebrow text-secondary">Care Plan</span>
          {isApproved && (
            <span className="care-plan-approved-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Approved{approvedByName ? ` by ${approvedByName}` : ''}
            </span>
          )}
        </div>
        {!canEdit ? null : !editingPlan ? (
          isApproved && (
            <button
              onClick={() => setEditingPlan(true)}
              style={{ background: 'none', border: '1px solid var(--wi-divider)', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 500, color: 'var(--wi-text-secondary)', cursor: 'pointer', fontFamily: 'var(--wi-font-family)', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </button>
          )
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => { setEditPlan(buildEditPlan(carePlan)); setEditingPlan(false); }}
              style={{ background: 'none', border: '1px solid var(--wi-divider)', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 500, color: 'var(--wi-text-secondary)', cursor: 'pointer', fontFamily: 'var(--wi-font-family)' }}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              style={{ fontSize: 11, padding: '4px 14px', borderRadius: 6, background: !isApproved ? '#F59E0B' : undefined }}
              disabled={planSaving}
              onClick={async () => { await onSaveCarePlan(); setEditingPlan(false); }}
            >
              {planSaving ? 'Saving...' : isApproved ? 'Save' : 'Approve & Save'}
            </button>
          </div>
        )}
      </div>

      <div
        className={!isApproved && !editingPlan ? 'care-plan-needs-review' : undefined}
        style={{ background: 'var(--wi-background-secondary)', borderRadius: 12, border: editingPlan ? '2px solid var(--wi-action-primary)' : isApproved ? '1px solid var(--wi-divider)' : undefined, overflow: 'hidden' }}
      >
        {/* ── Guideline pathway: applied-state info strip ── */}
        {(() => {
          const applied = appliedPathways;
          if (applied.length === 0) return null;
          const primaryId = applied.find(id => PATHWAY_META[id]?.meta) || applied[0];
          const primary = primaryId ? PATHWAY_META[primaryId] : null;
          const secondaries = applied.filter(id => id !== primaryId).map(id => PATHWAY_META[id]?.label || id);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', background: 'var(--wi-background-info)', color: 'var(--wi-text-primary-info)', borderBottom: '1px solid var(--wi-divider)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8 5.8 21.3l2.4-7.4L2 9.4h7.6z"/>
              </svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.75, marginBottom: 2 }}>
                  Guideline pathway
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                  {primary?.label || primaryId}
                  {primary?.guideline && (
                    <span style={{ fontWeight: 400, opacity: 0.8, marginLeft: 6 }}>· {primary.guideline}</span>
                  )}
                  {secondaries.length > 0 && (
                    <span style={{ fontWeight: 400, opacity: 0.8, marginLeft: 6, fontSize: 12 }}>
                      (covers {secondaries.join(', ')})
                    </span>
                  )}
                </div>
              </div>
              {canEdit && (
                <button
                  onClick={openPathway}
                  style={{ background: 'var(--wi-background-primary)', border: '1px solid currentColor', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, color: 'inherit', cursor: 'pointer', fontFamily: 'var(--wi-font-family)', whiteSpace: 'nowrap' }}
                >
                  Change pathway
                </button>
              )}
            </div>
          );
        })()}

        {/* ── Needs-review banner (unapproved plans) ── */}
        {!isApproved && !editingPlan && (
          <div className="care-plan-needs-review-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span>This care plan has not been reviewed yet</span>
          </div>
        )}

        {/* ── VIEW MODE ── */}
        {!editingPlan && (
          <div style={{ padding: '18px 20px' }}>
            {canEdit && appliedPathways.length === 0 && (
              <div style={{ background: 'var(--wi-background-primary)', border: '1px dashed var(--wi-divider)', borderRadius: 10, padding: '16px 18px', marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--wi-action-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8 5.8 21.3l2.4-7.4L2 9.4h7.6z"/>
                </svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--wi-text-primary)', marginBottom: 4 }}>
                    Select a guideline pathway
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--wi-text-secondary)', marginBottom: 12 }}>
                    Pick one or more (hypertension, CKD, obesity, diabetes…) to preconfigure this care plan automatically with default settings — targets, monitoring cadence, devices, content curriculum, and medication classes — pulled from AHA / ACC / ADA / KDIGO guidelines. You can still edit everything before approving.
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={openPathway} className="btn-primary">
                      Select pathway
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Clinical summary row */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 0', minWidth: 120 }}>
                <span style={fieldLabel}>Risk level</span>
                <span style={{ ...chipStyle, background: riskColor + '18', color: riskColor, fontWeight: 700, fontSize: 11, letterSpacing: '0.02em', marginBottom: 0 }}>
                  {(carePlan.risk_level || 'standard').charAt(0).toUpperCase() + (carePlan.risk_level || 'standard').slice(1)}
                </span>
              </div>
              <div style={{ flex: '2 1 0', minWidth: 160 }}>
                <span style={fieldLabel}>Conditions</span>
                {(carePlan.conditions || []).length > 0 ? (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {carePlan.conditions.map(c => chipRead(c, 'var(--wi-background-info)', 'var(--wi-text-primary-info)'))}
                  </div>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)' }}>—</span>
                )}
              </div>
              <div style={{ flex: '2 1 0', minWidth: 160 }}>
                <span style={fieldLabel}>Devices</span>
                {(carePlan.devices || []).length > 0 ? (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {carePlan.devices.map(d => {
                      const dl = String(d).toLowerCase();
                      const isBp = dl.includes('bpm') || dl.includes('blood pressure');
                      const isScale = dl.includes('body') || dl.includes('scale');
                      const icon = isBp ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 8h10a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/><circle cx="12" cy="12.5" r="1.2"/></svg>
                      ) : isScale ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="14" rx="2"/><path d="M7 11h10M9 14h6"/></svg>
                      ) : (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="3" width="14" height="18" rx="2.5"/><circle cx="12" cy="18" r="0.6" fill="currentColor"/></svg>
                      );
                      return (
                        <span key={d} style={{ ...chipStyle, gap: 5, background: 'var(--wi-background-primary)', color: 'var(--wi-text-secondary)' }}>
                          {icon}
                          {d}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)' }}>—</span>
                )}
              </div>
            </div>

            {/* Targets */}
            {planTargets.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <span style={fieldLabel}>Targets</span>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {planTargets.map(([key, val]) => {
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                    const rawVal = typeof val === 'object' && val !== null ? val : { value: val };
                    const displayVal = formatTargetDisplay(rawVal);
                    const unit = rawVal.unit || '';
                    return (
                      <div key={key} style={{ background: 'var(--wi-background-primary)', borderRadius: 8, padding: '8px 12px', flex: '0 1 auto', minWidth: 90, maxWidth: 220 }}>
                        <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)', display: 'block', marginBottom: 2 }}>{label}</span>
                        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--wi-text-primary)' }}>{displayVal}</span>
                        {unit && <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)', marginLeft: 3 }}>{unit}</span>}
                        {rawVal.guideline && <span style={{ fontSize: 10, color: 'var(--wi-text-secondary)', display: 'block', marginTop: 2 }}>{rawVal.guideline}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lifestyle + Exercise + Nutrition */}
            {(carePlan.lifestyle_focus || []).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <span style={fieldLabel}>Lifestyle focus</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {carePlan.lifestyle_focus.map(l => chipRead(l, 'var(--wi-background-good)', 'var(--wi-text-primary-good)'))}
                </div>
              </div>
            )}

            {(exercise.type || nutrition.pattern || nutrition.sodium_limit_mg) && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {exercise.type && (
                  <div style={{ flex: 1, minWidth: 160, background: 'var(--wi-background-primary)', borderRadius: 8, padding: '10px 14px' }}>
                    <span style={fieldLabel}>Exercise</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--wi-text-primary)', display: 'block' }}>
                      {exercise.type}{exercise.intensity ? ` · ${exercise.intensity}` : ''}
                    </span>
                    {(exercise.days_per_week || exercise.minutes_per_day) && (
                      <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)', display: 'block', marginTop: 2 }}>
                        {exercise.days_per_week ? `${exercise.days_per_week}x/wk` : ''}{exercise.days_per_week && exercise.minutes_per_day ? ' · ' : ''}{exercise.minutes_per_day ? `${exercise.minutes_per_day} min/day` : ''}
                      </span>
                    )}
                  </div>
                )}
                {(nutrition.pattern || nutrition.sodium_limit_mg) && (
                  <div style={{ flex: 1, minWidth: 160, background: 'var(--wi-background-primary)', borderRadius: 8, padding: '10px 14px' }}>
                    <span style={fieldLabel}>Nutrition</span>
                    {nutrition.pattern && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--wi-text-primary)', display: 'block' }}>{nutrition.pattern}</span>}
                    {nutrition.sodium_limit_mg && <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)', display: 'block', marginTop: 2 }}>Sodium limit: {nutrition.sodium_limit_mg} mg/day</span>}
                  </div>
                )}
              </div>
            )}

            {/* Medications */}
            {(carePlan.medications || []).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <span style={fieldLabel}>Medications</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {carePlan.medications.map((m, i) => (
                    <div key={i} style={{ background: 'var(--wi-background-primary)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--wi-text-primary)' }}>
                        {m.name}{m.dose ? ` · ${m.dose}` : ''}
                      </span>
                      {m.frequency && (
                        <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)' }}>{m.frequency}</span>
                      )}
                      {m.class && (
                        <span style={{ ...chipStyle, background: 'var(--wi-background-secondary)', color: 'var(--wi-text-secondary)', fontSize: 10, padding: '0 8px', height: 20, marginLeft: 'auto', marginRight: 0, marginBottom: 0 }}>
                          {m.class}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Bottom action bar (unapproved plans) ── */}
            {canEdit && !isApproved && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--wi-divider)' }}>
                <button className="care-plan-review-cta" onClick={() => setEditingPlan(true)}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Review &amp; Approve
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── EDIT MODE ── */}
        {canEdit && editingPlan && (
          <div style={{ padding: '18px 20px' }}>
            {/* Risk level */}
            <div style={{ marginBottom: 16 }}>
              <span style={fieldLabel}>Risk level</span>
              <select value={editPlan.risk_level} onChange={e => setEditPlan(p => ({ ...p, risk_level: e.target.value }))} className="patient-list-select" style={{ minWidth: 140 }}>
                {['standard', 'elevated', 'high', 'critical'].map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
              </select>
            </div>

            {/* Conditions */}
            <div style={{ marginBottom: 16 }}>
              <span style={fieldLabel}>Conditions</span>
              <ConstrainedChips items={editPlan.conditions} options={CONDITIONS} normalizeFn={normalizeCondition} onChange={v => setEditPlan(p => ({ ...p, conditions: v }))} chipBg="var(--wi-background-info)" chipColor="var(--wi-text-primary-info)" placeholder="Add condition..." labelFn={v => v} />
            </div>

            {/* Targets — editable */}
            <div style={{ marginBottom: 16 }}>
              <span style={fieldLabel}>Targets</span>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {Object.entries(editPlan.targets || {}).map(([key, val]) => {
                  const raw = typeof val === 'object' && val !== null ? val : { value: val };
                  return (
                    <div key={key} style={{ background: 'var(--wi-background-primary)', borderRadius: 8, padding: '10px 12px', flex: '1 1 0', minWidth: 130, position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)' }}>
                          {TARGET_DEFS[key]?.label || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        <button
                          onClick={() => setEditPlan(p => { const t = { ...p.targets }; delete t[key]; return { ...p, targets: t }; })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--wi-text-secondary)', padding: 0, lineHeight: 1 }}
                          title="Remove target"
                        >×</button>
                      </div>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <select
                          value={raw.operator || '≤'}
                          onChange={e => setEditPlan(p => ({
                            ...p,
                            targets: { ...p.targets, [key]: (() => { const next = { ...raw, operator: e.target.value }; return { ...next, display: formatTargetDisplay(next) }; })() },
                          }))}
                          className="patient-list-select"
                          style={{ width: 52, fontSize: 13, padding: '4px 6px' }}
                        >
                          {['<', '≤', '=', '≥', '>'].map(op => <option key={op} value={op}>{op}</option>)}
                        </select>
                        <input
                          type="number"
                          value={raw.value ?? ''}
                          onChange={e => setEditPlan(p => ({
                            ...p,
                            targets: { ...p.targets, [key]: (() => { const next = { ...raw, value: e.target.value === '' ? '' : Number(e.target.value) }; return { ...next, display: formatTargetDisplay(next) }; })() },
                          }))}
                          style={{ ...inputStyle, width: 70, padding: '4px 8px' }}
                        />
                        {raw.unit && <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)' }}>{raw.unit}</span>}
                      </div>
                    </div>
                  );
                })}
                {/* Add target dropdown */}
                {(() => {
                  const existingKeys = Object.keys(editPlan.targets || {});
                  const available = Object.entries(TARGET_DEFS).filter(([k]) => !existingKeys.includes(k));
                  if (!available.length) return null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 100, minHeight: 60, border: '1px dashed var(--wi-divider)', borderRadius: 8, padding: '4px 8px' }}>
                      <select
                        value=""
                        onChange={e => {
                          const k = e.target.value;
                          if (!k) return;
                          const def = TARGET_DEFS[k];
                          setEditPlan(p => ({
                            ...p,
                            targets: { ...p.targets, [k]: (() => { const next = { operator: def.operator, value: def.value ?? '', unit: def.unit, guideline: def.guideline || '' }; return { ...next, display: def.display || formatTargetDisplay(next) }; })() },
                          }));
                        }}
                        style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid var(--wi-divider)', fontSize: 12, fontFamily: 'var(--wi-font-family)', outline: 'none', background: 'var(--wi-background-secondary)', color: 'var(--wi-text-secondary)', cursor: 'pointer' }}
                      >
                        <option value="">+ Add target</option>
                        {available.map(([k, def]) => (
                          <option key={k} value={k}>{def.label} ({def.unit})</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Devices */}
            <div style={{ marginBottom: 16 }}>
              <span style={fieldLabel}>Devices</span>
              <ConstrainedChips items={editPlan.devices} options={DEVICES} onChange={v => setEditPlan(p => ({ ...p, devices: v }))} placeholder="Add device..." labelFn={v => v} />
            </div>

            {/* Lifestyle focus */}
            <div style={{ marginBottom: 16 }}>
              <span style={fieldLabel}>Lifestyle focus</span>
              <ConstrainedChips items={editPlan.lifestyle_focus} options={LIFESTYLE_FOCUS} onChange={v => setEditPlan(p => ({ ...p, lifestyle_focus: v }))} chipBg="var(--wi-background-good)" chipColor="var(--wi-text-primary-good)" placeholder="Add focus area..." />
            </div>

            {/* Exercise plan */}
            <div style={{ marginBottom: 16 }}>
              <span style={fieldLabel}>Exercise plan</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input value={editPlan.exercise_plan?.type || ''} onChange={e => setEditPlan(p => ({ ...p, exercise_plan: { ...p.exercise_plan, type: e.target.value } }))} placeholder="Type (e.g. walking)" style={{ ...inputStyle, flex: 1, minWidth: 120 }} />
                <select value={editPlan.exercise_plan?.intensity || 'moderate'} onChange={e => setEditPlan(p => ({ ...p, exercise_plan: { ...p.exercise_plan, intensity: e.target.value } }))} className="patient-list-select">
                  <option value="light">Light</option>
                  <option value="moderate">Moderate</option>
                  <option value="vigorous">Vigorous</option>
                </select>
                <input type="number" min="1" max="7" value={editPlan.exercise_plan?.days_per_week || ''} onChange={e => setEditPlan(p => ({ ...p, exercise_plan: { ...p.exercise_plan, days_per_week: Number(e.target.value) } }))} placeholder="Days/wk" style={{ ...inputStyle, width: 72 }} />
                <input type="number" min="5" max="120" value={editPlan.exercise_plan?.minutes_per_day || ''} onChange={e => setEditPlan(p => ({ ...p, exercise_plan: { ...p.exercise_plan, minutes_per_day: Number(e.target.value) } }))} placeholder="Min/day" style={{ ...inputStyle, width: 72 }} />
              </div>
            </div>

            {/* Nutrition plan */}
            <div>
              <span style={fieldLabel}>Nutrition plan</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input value={editPlan.nutrition_plan?.pattern || ''} onChange={e => setEditPlan(p => ({ ...p, nutrition_plan: { ...p.nutrition_plan, pattern: e.target.value } }))} placeholder="Pattern (e.g. DASH diet)" style={{ ...inputStyle, flex: 1, minWidth: 160 }} />
                <input type="number" min="500" max="5000" value={editPlan.nutrition_plan?.sodium_limit_mg || ''} onChange={e => setEditPlan(p => ({ ...p, nutrition_plan: { ...p.nutrition_plan, sodium_limit_mg: Number(e.target.value) } }))} placeholder="Sodium (mg)" style={{ ...inputStyle, width: 110 }} />
              </div>
            </div>

            {/* Medications */}
            <div style={{ marginTop: 16 }}>
              <span style={fieldLabel}>Medications</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(editPlan.medications || []).map((m, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      value={m.name || ''}
                      placeholder="Name (e.g. Lisinopril)"
                      onChange={e => setEditPlan(p => ({ ...p, medications: (p.medications || []).map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))}
                      style={{ ...inputStyle, flex: 2, minWidth: 140 }}
                    />
                    <input
                      value={m.dose || ''}
                      placeholder="Dose (e.g. 10mg)"
                      onChange={e => setEditPlan(p => ({ ...p, medications: (p.medications || []).map((x, j) => j === i ? { ...x, dose: e.target.value } : x) }))}
                      style={{ ...inputStyle, width: 100 }}
                    />
                    <input
                      value={m.frequency || ''}
                      placeholder="Frequency (e.g. daily, BID)"
                      onChange={e => setEditPlan(p => ({ ...p, medications: (p.medications || []).map((x, j) => j === i ? { ...x, frequency: e.target.value } : x) }))}
                      style={{ ...inputStyle, width: 150 }}
                    />
                    <select
                      value={m.class || ''}
                      onChange={e => setEditPlan(p => ({ ...p, medications: (p.medications || []).map((x, j) => j === i ? { ...x, class: e.target.value } : x) }))}
                      className="patient-list-select"
                      style={{ width: 150 }}
                    >
                      <option value="">Class…</option>
                      {MEDICATION_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button
                      onClick={() => setEditPlan(p => ({ ...p, medications: (p.medications || []).filter((_, j) => j !== i) }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--wi-text-secondary)', padding: '0 6px', lineHeight: 1 }}
                      title="Remove medication"
                    >×</button>
                  </div>
                ))}
                <button
                  onClick={() => setEditPlan(p => ({ ...p, medications: [...(p.medications || []), { name: '', dose: '', frequency: '', class: '' }] }))}
                  style={{ alignSelf: 'flex-start', marginTop: 4, padding: '6px 12px', borderRadius: 6, border: '1px dashed var(--wi-divider)', background: 'var(--wi-background-secondary)', color: 'var(--wi-text-secondary)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--wi-font-family)' }}
                >
                  + Add medication
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
